import {
  certificateExpiry,
  CertificateDomain,
  type Certificate,
  type CertificateKind,
} from "@cloudserver/contracts";
import { Link } from "@tanstack/react-router";
import { FileLock2, Pencil, Plus, RotateCw, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  useCertificates,
  useCreateCertificate,
  useDeleteCertificate,
  useRetryCertificate,
  useUpdateCertificate,
} from "../api/load-balancers.js";
import { progressOf, useRunningOperations } from "../api/operations.js";
import { useProject } from "../api/queries.js";
import { useCanWrite } from "../api/users.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError } from "../ui/feedback.js";
import { dateTime, relativeTime } from "../ui/format.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Dialog, type MenuItem } from "../ui/overlay.js";
import { Alert, Badge, Button, TextArea, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { formatLabels, parseLabels } from "./labels.js";
import { failureToast } from "./networks.js";

type T = ReturnType<typeof useTranslation>["t"];

/** Domains typed one per line or separated by commas or spaces. */
export const domainsOf = (text: string): string[] => [
  ...new Set(
    text
      .split(/[\s,]+/)
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean),
  ),
];

/** The badge of a certificate's state: issuance of managed ones first, then its expiry. */
export function certificateState(
  t: T,
  cert: Certificate,
): { tone: "ok" | "warn" | "danger" | "neutral"; text: string } {
  if (cert.status?.issuance === "pending")
    return { tone: "neutral", text: t("certificates.state.pending") };
  if (cert.status?.issuance === "failed")
    return { tone: "danger", text: t("certificates.state.failed") };
  if (cert.status?.renewal === "failed")
    return { tone: "danger", text: t("certificates.state.renewalFailed") };
  switch (certificateExpiry(cert)) {
    case "expired":
      return { tone: "danger", text: t("certificates.state.expired") };
    case "soon":
      return { tone: "warn", text: t("certificates.state.soon") };
    case "valid":
      return { tone: "ok", text: t("certificates.state.valid") };
    default:
      return { tone: "neutral", text: t("certificates.state.unknown") };
  }
}

function CreateDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateCertificate(projectId);
  const toast = useToast();
  const [kind, setKind] = useState<CertificateKind>("managed");
  const [name, setName] = useState("");
  const [domainsText, setDomainsText] = useState("");
  const [certificate, setCertificate] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [tried, setTried] = useState(false);
  const domains = domainsOf(domainsText);
  const badDomain = domains.find((d) => !CertificateDomain.safeParse(d).success);
  const managed = kind === "managed";
  const invalid =
    !name.trim() ||
    (managed
      ? domains.length === 0 || badDomain !== undefined
      : !certificate.includes("BEGIN CERTIFICATE") || !/BEGIN [A-Z ]*PRIVATE KEY/.test(privateKey));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (invalid) return;
    create.mutate(
      managed
        ? { kind: "managed", name: name.trim(), domains }
        : { kind: "uploaded", name: name.trim(), certificate, privateKey },
      {
        onSuccess: ({ certificate: created }) => {
          toast({
            tone: "ok",
            title: t(managed ? "certificates.requested" : "certificates.uploaded", {
              name: created.name,
            }),
          });
          // The key is not kept in the page once it has been sent.
          setPrivateKey("");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("certificates.create")}
      description={t("certificates.createLead")}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="create-certificate" busy={create.isPending}>
            {managed ? t("certificates.requestSubmit") : t("certificates.uploadSubmit")}
          </Button>
        </>
      }
    >
      <form id="create-certificate" onSubmit={submit} noValidate className="flex flex-col gap-4">
        <fieldset className="flex flex-col gap-2 text-sm">
          <legend className="mb-1 font-medium">{t("certificates.kind")}</legend>
          {(["managed", "uploaded"] as const).map((k) => (
            <label
              key={k}
              className="flex cursor-pointer items-start gap-2 rounded-lg border border-line px-3 py-2 hover:bg-surface-2"
            >
              <input
                type="radio"
                name="kind"
                checked={kind === k}
                onChange={() => setKind(k)}
                className="mt-0.5 size-4 accent-accent"
              />
              <span className="flex flex-col">
                <span className="font-medium">{t(`certificates.kinds.${k}`)}</span>
                <span className="text-muted">{t(`certificates.kindHints.${k}`)}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <TextField
          label={t("certificates.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={128}
          hint={t("certificates.nameHint")}
          error={
            tried && !name.trim() ? t("errors.fieldInvalid") : fieldError(create.error, "name", t)
          }
        />
        {managed ? (
          <>
            <TextArea
              label={t("certificates.domains")}
              value={domainsText}
              onChange={(e) => setDomainsText(e.target.value)}
              rows={3}
              spellCheck={false}
              placeholder={"example.com\nwww.example.com"}
              hint={t("certificates.domainsHint")}
              error={
                tried && (domains.length === 0 || badDomain)
                  ? t("certificates.domainsInvalid", { domain: badDomain ?? "" })
                  : fieldError(create.error, "domains", t)
              }
            />
            <Alert tone="neutral">{t("certificates.managedDnsNote")}</Alert>
          </>
        ) : (
          <>
            <TextArea
              label={t("certificates.certificate")}
              value={certificate}
              onChange={(e) => setCertificate(e.target.value)}
              rows={5}
              spellCheck={false}
              className="font-mono text-xs"
              placeholder="-----BEGIN CERTIFICATE-----"
              hint={t("certificates.certificateHint")}
              error={
                tried && !certificate.includes("BEGIN CERTIFICATE")
                  ? t("certificates.pemInvalid")
                  : fieldError(create.error, "certificate", t)
              }
            />
            <TextArea
              label={t("certificates.privateKey")}
              value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              rows={5}
              spellCheck={false}
              autoComplete="off"
              className="font-mono text-xs"
              placeholder="-----BEGIN PRIVATE KEY-----"
              hint={t("certificates.privateKeyHint")}
              error={
                tried && !/BEGIN [A-Z ]*PRIVATE KEY/.test(privateKey)
                  ? t("certificates.keyInvalid")
                  : fieldError(create.error, "privateKey", t)
              }
            />
          </>
        )}
        <ErrorMessage error={create.error} />
      </form>
    </Dialog>
  );
}

function EditDialog({
  projectId,
  cert,
  onClose,
}: {
  projectId: string;
  cert: Certificate;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateCertificate(projectId);
  const [name, setName] = useState(cert.name);
  const [labelsText, setLabelsText] = useState(formatLabels(cert.labels));
  const [badLine, setBadLine] = useState<number | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseLabels(labelsText);
    setBadLine(parsed.badLine);
    if (!parsed.labels) return;
    update.mutate(
      { id: cert.id, name: name.trim(), labels: parsed.labels },
      { onSuccess: onClose },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("certificates.editTitle", { name: cert.name })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="edit-certificate" busy={update.isPending}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form id="edit-certificate" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("certificates.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={128}
          error={fieldError(update.error, "name", t)}
        />
        <TextArea
          label={t("certificates.labels")}
          value={labelsText}
          onChange={(e) => setLabelsText(e.target.value)}
          rows={3}
          spellCheck={false}
          placeholder="env=production"
          hint={t("networks.labelsHint")}
          error={badLine !== null ? t("networks.labelsInvalid", { line: badLine }) : undefined}
        />
        <ErrorMessage error={update.error} />
      </form>
    </Dialog>
  );
}

type Open = { kind: "edit" | "delete"; cert: Certificate } | null;

export function CertificatesPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const project = useProject(projectId);
  const canWrite = useCanWrite(projectId);
  const certs = useCertificates(projectId);
  const running = useRunningOperations(projectId);
  const retry = useRetryCertificate(projectId);
  const remove = useDeleteCertificate(projectId);
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<Open>(null);
  const readOnly = project.data?.connection?.permission === "read";
  const readOnlyReason = t("errors.reasons.read_only_token");
  const close = () => setOpen(null);

  const createButton = (
    <Button
      onClick={() => setCreating(true)}
      disabled={readOnly}
      icon={<Plus className="size-4" aria-hidden />}
    >
      {t("certificates.create")}
    </Button>
  );

  const menu = (cert: Certificate): MenuItem[][] => {
    const busy = progressOf(running.data, cert.id) !== null;
    const failed = cert.status?.issuance === "failed" || cert.status?.renewal === "failed";
    const inUse = cert.usedBy.length > 0;
    const why = (fallback: string) =>
      readOnly ? readOnlyReason : busy ? t("servers.busy") : fallback;
    return [
      ...(cert.kind === "managed"
        ? [
            [
              {
                label: t("certificates.retry"),
                icon: <RotateCw className="size-4" aria-hidden />,
                disabled: readOnly || busy || !failed,
                reason: why(t("certificates.why.notFailed")),
                onSelect: () =>
                  retry.mutate(cert.id, {
                    onSuccess: () =>
                      toast({ tone: "ok", title: t("certificates.retrying", { name: cert.name }) }),
                    onError: (error) => toast(failureToast(t, error)),
                  }),
              },
            ],
          ]
        : []),
      [
        {
          label: t("certificates.edit"),
          icon: <Pencil className="size-4" aria-hidden />,
          disabled: readOnly || busy,
          reason: why(""),
          onSelect: () => setOpen({ kind: "edit", cert }),
        },
      ],
      [
        {
          label: t("certificates.delete"),
          icon: <Trash2 className="size-4" aria-hidden />,
          danger: true,
          disabled: readOnly || busy || inUse,
          reason: why(t("certificates.why.inUse")),
          onSelect: () => setOpen({ kind: "delete", cert }),
        },
      ],
    ];
  };

  const deleting = open?.kind === "delete" ? open.cert : null;
  return (
    <>
      <PageHeader
        title={t("nav.certificates")}
        intro={t("certificates.intro")}
        help="certificates"
        actions={canWrite && certs.data?.length ? createButton : undefined}
      />
      {readOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {certs.error ? (
        <ErrorMessage error={certs.error} />
      ) : (
        <DataTable<Certificate>
          label={t("nav.certificates")}
          rows={certs.data}
          rowKey={(c) => c.id}
          initialSort={{ key: "name", dir: "asc" }}
          progress={(c) => progressOf(running.data, c.id)}
          empty={
            <EmptyState
              icon={<FileLock2 className="size-6" aria-hidden />}
              title={t("certificates.empty")}
              action={canWrite ? createButton : undefined}
              help="certificates"
            >
              {t("certificates.emptyLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("certificates.name"),
              primary: true,
              sort: (c) => c.name.toLowerCase(),
              cell: (c) => (
                <span className="flex min-w-0 flex-col">
                  <span className="font-medium">{c.name}</span>
                  <span className="truncate text-xs text-muted" title={c.domains.join(", ")}>
                    {c.domains.join(", ") || "—"}
                  </span>
                </span>
              ),
            },
            {
              key: "kind",
              header: t("certificates.kind"),
              sort: (c) => c.kind,
              cell: (c) => <Badge>{t(`certificates.kinds.${c.kind}`)}</Badge>,
            },
            {
              key: "state",
              header: t("certificates.stateTitle"),
              cell: (c) => {
                const state = certificateState(t, c);
                return (
                  <span className="flex flex-col gap-1">
                    <Badge tone={state.tone}>{state.text}</Badge>
                    {c.status?.error && (
                      <span className="text-xs text-danger">{c.status.error.message}</span>
                    )}
                  </span>
                );
              },
            },
            {
              key: "expires",
              header: t("certificates.expires"),
              sort: (c) => c.notValidAfter ?? "",
              cell: (c) =>
                c.notValidAfter ? (
                  <span className="flex flex-col">
                    <span className="whitespace-nowrap">
                      {dateTime(c.notValidAfter, i18n.language)}
                    </span>
                    <span className="text-xs text-muted">
                      {relativeTime(c.notValidAfter, i18n.language)}
                    </span>
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                ),
            },
            {
              key: "usedBy",
              header: t("certificates.usedBy"),
              cell: (c) =>
                c.usedBy.length > 0 ? (
                  <span className="flex flex-wrap gap-x-2">
                    {c.usedBy.map((lb) =>
                      lb.id ? (
                        <Link
                          key={lb.id}
                          to="/projects/$projectId/load-balancers/$loadBalancerId"
                          params={{ projectId, loadBalancerId: lb.id }}
                          search={{ tab: "services" }}
                          className="text-accent hover:underline"
                        >
                          {lb.name}
                        </Link>
                      ) : (
                        <span key={lb.name}>{lb.name}</span>
                      ),
                    )}
                  </span>
                ) : (
                  <span className="whitespace-nowrap text-muted">{t("certificates.unused")}</span>
                ),
            },
            {
              key: "created",
              header: t("certificates.createdAt"),
              hideOnPhone: true,
              sort: (c) => c.createdAt,
              cell: (c) => (
                <span className="text-muted">{relativeTime(c.createdAt, i18n.language)}</span>
              ),
            },
          ]}
          rowMenu={menu}
        />
      )}
      {creating && <CreateDialog projectId={projectId} onClose={() => setCreating(false)} />}
      {open?.kind === "edit" && (
        <EditDialog projectId={projectId} cert={open.cert} onClose={close} />
      )}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && close()}
        title={t("certificates.deleteTitle", { name: deleting?.name ?? "" })}
        consequences={[
          t("certificates.deleteGone"),
          ...(deleting?.kind === "uploaded" ? [t("certificates.deleteKey")] : []),
        ]}
        confirmText={deleting?.name ?? ""}
        actionLabel={t("certificates.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("certificates.deleted", { name: deleting.name }) });
              close();
            },
            onError: (error) => toast(failureToast(t, error)),
          })
        }
      />
    </>
  );
}
